import { useEffect, useState, type ReactNode } from 'react';
import { LuHistory } from 'react-icons/lu';
import { PanelSection } from '../RecordPanel';
import {
    HistoricoService,
    type EntidadeHistorico,
    type EventoHistorico,
    type FonteHistorico,
    type Historico,
} from '../../services/HistoricoService';
import { PERMISSION_LABELS } from '../../utils/permissionLabels';
import './SecaoHistorico.css';

/* O histórico de um registro: quem criou, quem mudou o quê e quando, quem
   excluiu — e, na oportunidade, a jornada (chegada, primeiras mensagens,
   etapas). Duas casas (docs/propostas/historico.html, seção 4):
   - painel: seção no fim do RecordPanel, as 5 mais recentes e "Ver tudo",
     no molde da de Sistema › Funcionalidades;
   - pagina: o corpo de uma aba (a ficha da oportunidade), sem título — a aba
     ativa já diz o que é.
   Busca ao montar: o painel desmonta a seção ao editar e volta a montar ao
   salvar, então o evento recém-gravado já aparece. */

const LIMITE_INICIAL = 5;
const LIMITE_PAGINA = 30;
const LIMITE_TUDO = 300;

/* Do ano corrente, dia e hora; de antes, só a data com o ano — as duas cabem
   na coluna de 86px sem quebrar. */
const quando = (iso: string) => {
    const d = new Date(iso);
    if (d.getFullYear() !== new Date().getFullYear()) return d.toLocaleDateString('pt-BR');
    const dia = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    return `${dia} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

/** Quem fez, em texto de tela. Modo suporte, job e webhook aparecem como "Sistema" (decisão de 07/10/2026). */
const quem = (e: EventoHistorico): string => {
    switch (e.origem) {
        case 'usuario': return e.autor ?? 'Alguém';
        case 'brik_ai': return e.autor ? `${e.autor}, pelo Brik AI,` : 'O Brik AI';
        case 'ia': return 'Agente de IA';
        case 'canal': return 'Cliente, pelo canal,';
        case 'integracao': return 'Integração';
        case 'backfill': return e.autor ?? '';
        default: return 'Sistema';
    }
};

/* Evento de outro registro, visto daqui: no contato aparecem as oportunidades e
   organizações dele; na oportunidade, as tarefas, ligações, documentos e anexos.
   A frase diz de qual registro é. */
const ROTULO_FILHO: Partial<Record<EntidadeHistorico, string>> = {
    oportunidade: 'Oportunidade',
    organizacao: 'Organização',
    contato: 'Contato',
    tarefa: 'Tarefa',
    ligacao: 'Ligação',
    documento: 'Documento',
};

const valor = (v: string | null) => v ?? <span className="hst-vazio">vazio</span>;

const frase = (e: EventoHistorico, vista: EntidadeHistorico): ReactNode => {
    const q = quem(e);
    const detalhe = e.detalhe ? <b>{e.detalhe}</b> : null;
    const por = e.detalhe ? <span className="hst-fnt"> · {e.detalhe}</span> : null;

    if (e.entidade !== vista) {
        /* A pessoa entrou ou saiu da organização: daqui, o que importa é qual organização. */
        if (e.entidade === 'organizacao' && (e.tipo === 'vinculado' || e.tipo === 'desvinculado')) {
            const org = <b>{e.entidadeNome ?? 'uma organização'}</b>;
            return e.tipo === 'vinculado' ? <>{q} adicionou à organização {org}</> : <>{q} removeu da organização {org}</>;
        }
        const rotulo = ROTULO_FILHO[e.entidade] ?? e.entidade;
        const nome = e.entidadeNome ? ` ${e.entidadeNome}` : '';
        return <><span className="hst-filho">{rotulo}{nome}</span> {frase(e, e.entidade)}</>;
    }

    switch (e.tipo) {
        case 'criado':
            /* Nasceu sozinho (mensagem do cliente, IA, formulário, webhook): "chegou", não "criou".
               Backfill sem autor: a linha antiga só sabia a data. O detalhe diz por onde
               ("importação de planilha"; na oportunidade, o canal). */
            if (e.origem === 'canal' || e.origem === 'ia' || e.origem === 'integracao') return <>Chegou{por}</>;
            /* Oportunidade reconstruída: a linha antiga só sabe por onde veio, então "chegou". */
            if (e.origem === 'backfill' && e.entidade === 'oportunidade' && !e.autor) return <>Chegou{por}</>;
            return q ? <>{q} criou{por}</> : <>Criado{por}</>;
        case 'excluido':
            return <>{q} excluiu{por}</>;
        case 'acao':
            /* Ação sem campo: o detalhe é a frase ("reenviou o convite"). */
            return <>{q} {e.detalhe}</>;
        case 'vinculado':
            return <>{q} adicionou {detalhe}</>;
        case 'desvinculado':
            return <>{q} removeu {detalhe}</>;
        case 'editado':
            if (e.mudancas.length === 1) {
                const m = e.mudancas[0];
                return <>{q} alterou <b>{rotuloDoCampo(m.campo, m.rotulo).toLowerCase()}</b>{por}</>;
            }
            return <>{q} alterou {e.mudancas.length} campos{por}</>;
        /* Jornada da oportunidade */
        case 'primeira_mensagem_cliente':
            return <>Cliente mandou a primeira mensagem</>;
        case 'primeira_mensagem_ia':
            return <>Agente de IA respondeu pela primeira vez</>;
        case 'primeira_mensagem_vendedor':
            return <>{e.autor ?? 'O vendedor'} mandou a primeira mensagem</>;
        case 'fechado': {
            const desfecho = e.mudancas[0]?.depois;
            return <>Fechada como <b>{desfecho ?? 'encerrada'}</b></>;
        }
        case 'motivo_perda':
            return <>Motivo de perda: {detalhe ?? <span className="hst-vazio">sem motivo</span>}</>;
        default:
            return <>{q} {e.tipo.replace(/_/g, ' ')}{por}</>;
    }
};

/* Permissão de perfil: a API guarda a chave técnica ("gestao_motivo_perda"); a tela de
   Perfis mostra o rótulo do mesmo mapa usado aqui. */
const rotuloDoCampo = (campo: string, rotulo: string) =>
    campo.startsWith('perm:') ? (PERMISSION_LABELS[rotulo] ?? rotulo) : rotulo;

/* De → para de cada campo. Na criação (e no fechamento) não há "antes": vai só o valor. */
const Mudancas = ({ e }: { e: EventoHistorico }) => {
    if (e.mudancas.length === 0 || e.tipo === 'fechado') return null;
    const soDepois = e.tipo === 'criado';
    return (
        <ul className="hst-mud">
            {e.mudancas.map(m => (
                <li key={m.campo}>
                    <span className="hst-rot">{rotuloDoCampo(m.campo, m.rotulo)}</span>
                    {m.antes === null && m.depois === null
                        ? <span className="hst-vazio">alterado</span>
                        : soDepois
                            ? <b>{valor(m.depois)}</b>
                            : <><span className="hst-antes">{valor(m.antes)}</span> → <b>{valor(m.depois)}</b></>}
                </li>
            ))}
        </ul>
    );
};

const SecaoHistorico = ({ entidade, id, variante = 'painel', fonte = 'registro' }: {
    entidade: EntidadeHistorico;
    /** Sem id quando a fonte é a tela inteira (Metas, Tarefas padrão). */
    id?: number;
    variante?: 'painel' | 'pagina';
    fonte?: FonteHistorico;
}) => {
    const [historico, setHistorico] = useState<Historico | null>(null);
    const [erro, setErro] = useState('');
    const [tudo, setTudo] = useState(false);
    const inicial = variante === 'pagina' ? LIMITE_PAGINA : LIMITE_INICIAL;

    useEffect(() => {
        let vivo = true;
        setErro('');
        HistoricoService.buscar(entidade, id, tudo ? LIMITE_TUDO : inicial, fonte)
            .then(h => { if (vivo) setHistorico(h); })
            .catch((ex: Error) => { if (vivo) { setHistorico(null); setErro(ex.message); } });
        return () => { vivo = false; };
    }, [entidade, id, tudo, inicial, fonte]);

    const eventos = historico?.eventos ?? [];
    const faltam = historico ? historico.total - eventos.length : 0;
    const verTudo = faltam > 0 && (
        <button type="button" className="hst-tudo" onClick={() => setTudo(true)}>
            <LuHistory size={13} /> Ver tudo ({historico!.total})
        </button>
    );

    const corpo = (
        <div className="hst">
            {erro && <p className="hst-nada">Não foi possível carregar o histórico. {erro}</p>}
            {!erro && !historico && <p className="hst-nada">Carregando…</p>}
            {!erro && historico && eventos.length === 0 && <p className="hst-nada">Nada registrado ainda.</p>}
            {eventos.length > 0 && (
                <ol className="hst-lista">
                    {eventos.map(e => (
                        <li key={e.id} className="hst-linha">
                            <span className="hst-quando">{quando(e.em)}</span>
                            <div className="hst-texto">
                                <span>
                                    {frase(e, entidade)}
                                    {e.origem === 'backfill' && <span className="hst-fnt"> · registro anterior ao histórico</span>}
                                </span>
                                <Mudancas e={e} />
                            </div>
                        </li>
                    ))}
                </ol>
            )}
            {variante === 'pagina' && verTudo && <div className="hst-rodape">{verTudo}</div>}
        </div>
    );

    if (variante === 'pagina') return corpo;
    return <PanelSection title="Histórico" extra={verTudo}>{corpo}</PanelSection>;
};

export default SecaoHistorico;
